import { formatDateKey, getMonthDays } from '../../../components/calendar/calendar-utils';
import type { DayCounts } from '../../../components/calendar/day-marker';
import type { DayStatus } from '../../../components/calendar/weel-day-row';

const VALID_DAY_STATUSES: Set<string> = new Set<DayStatus>([
  'complete',
  'partial',
  'nonCompliant',
  'missing',
  'notRequired',
  'future',
]);

export function parseDayStatus(raw: string | undefined): DayStatus {
  if (raw && VALID_DAY_STATUSES.has(raw)) {
    return raw as DayStatus;
  }
  return 'missing';
}

export function getMonthDateRange(month: Date): { fromDate: string; toDate: string } {
  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const firstDay = new Date(year, monthIndex, 1);
  const lastDay = new Date(year, monthIndex + 1, 0);
  return { fromDate: formatDateKey(firstDay), toDate: formatDateKey(lastDay) };
}

export interface SimpleCalendarData {
  calendar: Record<string, string>;
}

export interface OilCalendarData {
  days: Record<string, { status: string }>;
}

export function mapWeekStatuses(
  weekDays: Date[],
  data: SimpleCalendarData | undefined,
): Map<number, DayStatus> {
  const statusMap = new Map<number, DayStatus>();
  for (let i = 0; i < weekDays.length; i++) {
    const dayKey = formatDateKey(weekDays[i]);
    const status = parseDayStatus(data?.calendar[dayKey]);
    statusMap.set(i, status);
  }
  return statusMap;
}

export function mapMonthStatuses(
  month: Date,
  data: SimpleCalendarData | undefined,
): Map<string, DayStatus> {
  const statusMap = new Map<string, DayStatus>();
  for (const dayKey of getMonthDays(month).map(formatDateKey)) {
    statusMap.set(dayKey, parseDayStatus(data?.calendar[dayKey]));
  }
  return statusMap;
}

export function mapOilWeekStatuses(
  weekDays: Date[],
  data: OilCalendarData | undefined,
): Map<number, DayStatus> {
  const statusMap = new Map<number, DayStatus>();
  for (let i = 0; i < weekDays.length; i++) {
    const dayKey = formatDateKey(weekDays[i]);
    const status = parseDayStatus(data?.days[dayKey]?.status);
    statusMap.set(i, status);
  }
  return statusMap;
}

export function mapOilMonthStatuses(
  month: Date,
  data: OilCalendarData | undefined,
): Map<string, DayStatus> {
  const statusMap = new Map<string, DayStatus>();
  for (const dayKey of getMonthDays(month).map(formatDateKey)) {
    statusMap.set(dayKey, parseDayStatus(data?.days[dayKey]?.status));
  }
  return statusMap;
}

/** Reads one day's counts out of a backend calendar response. */
export type DayCountsSource = (dayKey: string) => DayCounts | undefined;

/** Modules with a cadence: the backend's `scheduledCount` / `completedCount` for the day. */
export function scheduledDayCountsSource(
  days: Record<string, { scheduledCount: number; completedCount: number }> | undefined,
): DayCountsSource {
  return (dayKey) => {
    const day = days?.[dayKey];
    return day ? { planned: day.scheduledCount, done: day.completedCount } : undefined;
  };
}

/** On-demand modules: nothing is planned, `done` is the number of records of the day. */
export function recordDayCountsSource(
  days: Record<string, { recordCount: number }> | undefined,
): DayCountsSource {
  return (dayKey) => {
    const day = days?.[dayKey];
    return day ? { planned: 0, done: day.recordCount } : undefined;
  };
}

export function mapWeekDayCounts(
  weekDays: Date[],
  source: DayCountsSource,
): Map<number, DayCounts> {
  const countsByIndex = new Map<number, DayCounts>();
  weekDays.forEach((day, index) => {
    const counts = source(formatDateKey(day));
    if (counts) {
      countsByIndex.set(index, counts);
    }
  });
  return countsByIndex;
}

export function mapMonthDayCounts(month: Date, source: DayCountsSource): Map<string, DayCounts> {
  const countsByDayKey = new Map<string, DayCounts>();
  for (const dayKey of getMonthDays(month).map(formatDateKey)) {
    const counts = source(dayKey);
    if (counts) {
      countsByDayKey.set(dayKey, counts);
    }
  }
  return countsByDayKey;
}
